import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import EventGalleryImageCard from '../components/EventGalleryImageCard';
import GalleryLightbox from '../components/GalleryLightbox';
import styles from './OrganizerEventGalleryAdminPage.module.css';

const OrganizerEventGalleryAdminPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // image id
  const isGlobalMode = !id;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const pendingImages = images.filter((img) => img.status !== 'approved');
  const approvedImages = images.filter((img) => img.status === 'approved');

  const fetchPending = useCallback(() => {
    setLoading(true);
    setError('');
    const endpoint = isGlobalMode
      ? `${process.env.REACT_APP_API_URL}/organizer/memories/pending`
      : `${process.env.REACT_APP_API_URL}/organizer/events/${id}/memories/pending`;

    axios.get(endpoint)
      .then(res => {
        const data = Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
        setImages(data);
      })
        .catch(() => setError('画像一覧の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [id, isGlobalMode]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const friendlyModerationError = (status, raw) => {
    if (status === 403) return '権限がありません。主催者アカウントを確認してください。';
    if (status === 404) return '対象画像が見つかりません。再読み込みしてください。';
    if (status === 422) return raw || '操作内容が不正です。';
    if (status === 500) return '処理中にエラーが発生しました。時間をおいて再試行してください。';
    return raw || '操作に失敗しました。再度お試しください。';
  };

  const executeAction = async (img, action) => {
    const eventPathId = img?.event_path_id || id || img?.event_id;
    if (!eventPathId) {
      alert('イベント識別子が取得できないため操作できません。再読み込みしてください。');
      return;
    }
    setActionLoading(img.id);
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/organizer/events/${eventPathId}/memories/${img.id}`,
        { action },
        undefined
      );
      if (action === 'reject' || action === 'delete') {
        setImages(prev => prev.filter(i => i.id !== img.id));
      } else {
        const updated = res?.data?.data ?? res?.data ?? null;
        if (updated?.id) {
          setImages(prev => prev.map((item) => (item.id === img.id ? { ...item, ...updated } : item)));
        } else {
          fetchPending();
        }
      }
    } catch (e) {
      const status = e?.response?.status;
      const raw = e?.response?.data?.message;
      alert(friendlyModerationError(status, raw));
    } finally {
      setActionLoading(null);
    }
  };

  const runAction = async (img, action) => {
    if (action === 'delete') {
      setDeleteTarget(img);
      return;
    }

    await executeAction(img, action);
  };

  const closeDeleteModal = () => {
    if (actionLoading && deleteTarget && actionLoading === deleteTarget.id) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await executeAction(deleteTarget, 'delete');
    setDeleteTarget(null);
  };

  const openLightbox = (index) => {
    if (!images.length) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const renderImageSection = (title, sectionImages, emptyMessage) => {
    if (sectionImages.length === 0) {
      return (
        <div className={styles.card}>
          <h2 className={styles.sectionHeading}>{title}</h2>
          <p className={styles.emptyMsg}>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <section className={styles.imageSection}>
        <h2 className={styles.sectionHeading}>{title}</h2>
        <div className={styles.grid}>
          {sectionImages.map((img) => {
            const imageIndex = images.findIndex((item) => item.id === img.id);
            return (
              <div key={img.id} className={styles.cardWrapper}>
                {isGlobalMode && (
                  <div className={styles.eventLabelRow}>
                    <span className={styles.eventLabel}>{img.event_name || `イベント#${img.event_id}`}</span>
                    {img.event_path_id && (
                      <Link className={styles.eventLink} to={`/events/${img.event_path_id}/gallery-admin`}>
                        このイベントのみ
                      </Link>
                    )}
                  </div>
                )}
                <div className={styles.statusRow}>
                  <span className={`${styles.statusBadge} ${img.status === 'approved' ? styles.statusApproved : styles.statusPending}`}>
                    {img.status === 'approved' ? '承認済み' : '承認待ち'}
                  </span>
                </div>
                <EventGalleryImageCard image={img} onImageClick={() => openLightbox(imageIndex)} />
                <div className={styles.actions}>
                  <button
                    className={styles.approveBtn}
                    onClick={() => runAction(img, 'approve')}
                    disabled={actionLoading === img.id || img.status === 'approved'}
                  >{img.status === 'approved' ? '承認済み' : '承認'}</button>
                  <button
                    className={styles.rejectBtn}
                    onClick={() => runAction(img, 'reject')}
                    disabled={actionLoading === img.id}
                  >却下</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  if (!user) return <p>認証が必要です。</p>;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* ページヘッダー */}
        <div className={styles.pageHeader}>
          <h1 className={styles.heading}>
            {isGlobalMode ? 'ギャラリー承認管理（全イベント）' : 'ギャラリー承認管理'}
          </h1>
          <p className={styles.desc}>承認待ちと承認済みの公開画像を管理できます。承認済み画像も却下に戻せます。</p>
        </div>

        {/* ツールバー */}
        <div className={styles.toolbar}>
          <button className={styles.reloadBtn} onClick={fetchPending} disabled={loading}>
            再読み込み
          </button>
          <span className={styles.count}>承認待ち: {pendingImages.length}件 / 承認済み: {approvedImages.length}件</span>
        </div>

        {/* 状態表示 */}
        {loading && <p className={styles.stateText}>読み込み中...</p>}
        {error && <p className={styles.errorMsg}>{error}</p>}

        {/* 画像なし */}
        {!loading && !error && images.length === 0 && (
          <div className={styles.card}>
            <p className={styles.emptyMsg}>管理対象の画像はありません。</p>
          </div>
        )}

        {images.length > 0 && (
          <>
            {renderImageSection('未承認画像', pendingImages, '未承認画像はありません。')}
            {renderImageSection('承認済み画像', approvedImages, '承認済み画像はありません。')}
          </>
        )}
      </div>

      <GalleryLightbox
        images={images}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {deleteTarget && (
        <div className={styles.modalBackdrop} onClick={closeDeleteModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="画像削除確認">
            <h3 className={styles.modalTitle}>画像を削除しますか？</h3>
            <p className={styles.modalText}>この操作は取り消せません。</p>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={confirmDelete}
                disabled={actionLoading === deleteTarget.id}
              >
                {actionLoading === deleteTarget.id ? '削除中...' : '削除する'}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={closeDeleteModal}
                disabled={actionLoading === deleteTarget.id}
              >
                戻る
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerEventGalleryAdminPage;
